import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { stringify } from 'qs';
import { map, Observable, tap, throwError } from 'rxjs';
import { JsonApiManyIdentifiersDocument } from './classes/json-api-many-identifiers-document';
import { JsonApiManyResourcesDocument } from './classes/json-api-many-resources-document';
import { JsonApiOneIdentifierDocument } from './classes/json-api-one-identifier-document';
import { JsonApiOneResourceDocument } from './classes/json-api-one-resource-document';
import { JsonApiResource } from './classes/json-api-resource';
import { JsonApiDocumentInterface } from './interfaces/json-api-document';
import { JsonApiIdentifierInterface } from './interfaces/json-api-identifier-interface';
import { JsonApiParameters } from './interfaces/json-api-parameters';
import { JsonApiRelationships } from './interfaces/json-api-relationships';
import { JSON_API_URL } from './json-api-url';
import { ResourceType } from './utils/resource-type';

@Injectable({
  providedIn: 'root',
})
export class JsonApiService {
  constructor(
    protected http: HttpClient,
    @Inject(JSON_API_URL) protected url: string
  ) {}

  all<R extends JsonApiResource>(
    resourceType: ResourceType<R> | string,
    params?: JsonApiParameters
  ): Observable<JsonApiManyResourcesDocument<R>> {
    const type = this.getResourceType(resourceType);
    return this.http
      .get<JsonApiDocumentInterface<R[]>>(`${this.url}/${type}`, {
        params: new HttpParams({
          fromString: stringify(params),
        }),
      })
      .pipe(map((document) => new JsonApiManyResourcesDocument(document)));
  }

  find<R extends JsonApiResource>(
    resourceType: ResourceType<R>,
    id: string,
    params?: JsonApiParameters
  ): Observable<JsonApiOneResourceDocument<R>> {
    const type = this.getResourceType(resourceType);
    return this.http
      .get<JsonApiDocumentInterface<R>>(`${this.url}/${type}/${id}`, {
        params: new HttpParams({
          fromString: stringify(params),
        }),
      })
      .pipe(map((document) => new JsonApiOneResourceDocument(document)));
  }

  save<R extends JsonApiResource>(
    resource: R
  ): Observable<JsonApiOneResourceDocument<R>> {
    if (!resource.type) {
      throw new Error('You can not save a resource without type');
    }

    const body: Partial<JsonApiResource> = { type: resource.type };
    if (resource.id) {
      body.id = resource.id;
    }
    if (resource.attributes && Object.keys(resource.attributes).length > 0) {
      body.attributes = resource.attributes;
    }
    if (
      resource.relationships &&
      Object.keys(resource.relationships).length > 0
    ) {
      body.relationships = this.extractRelationshipsIdentifiers(
        resource.relationships
      );
    }
    return this.http
      .post<JsonApiDocumentInterface<R>>(`${this.url}/${resource.type}`, {
        data: body,
      })
      .pipe(
        map((document) => {
          if (document && document.data) {
            resource.id = document.data.id;
            resource.attributes = document.data.attributes;
            resource.links = document.data.links;
            resource.meta = document.data.meta;
          }
          return new JsonApiOneResourceDocument(document);
        })
      );
  }

  update<R extends JsonApiResource>(
    resource: R
  ): Observable<JsonApiOneResourceDocument<R>> {
    if (!resource.type) {
      throw new Error('You can not update a resource without type');
    }
    if (!resource.id) {
      throw new Error('You can not update a resource without id');
    }

    const body: Partial<JsonApiResource> = {
      type: resource.type,
      id: resource.id,
    };
    if (resource.attributes && Object.keys(resource.attributes).length > 0) {
      body.attributes = resource.attributes;
    }
    if (
      resource.relationships &&
      Object.keys(resource.relationships).length > 0
    ) {
      body.relationships = this.extractRelationshipsIdentifiers(
        resource.relationships
      );
    }

    return this.http
      .patch<JsonApiDocumentInterface<R>>(
        `${this.url}/${resource.type}/${resource.id}`,
        {
          data: body,
        }
      )
      .pipe(
        map((document) => {
          if (document && document.data) {
            resource.meta = document.data.meta;
            if (document.data.attributes) {
              resource.attributes = document.data.attributes;
            }
          }
          return new JsonApiOneResourceDocument(document);
        })
      );
  }

  batch(
    resource: { type: string; attributes?: any; relationships?: any },
    path: string,
    ids: string
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.url}/${path}?filter[equal.id]=${ids}`,
      {
        data: resource,
      }
    );
  }

  changeWorkflowState<R extends JsonApiResource> (
    resource: R,
    transition: string,
  ): Observable<JsonApiOneResourceDocument<R>> {
    if (!resource.type) {
      throw new Error('You can not update a resource without type');
    }
    if (!resource.id) {
      throw new Error('You can not update a resource without id');
    }

    const body: Partial<JsonApiResource> = {
      type: resource.type,
      id: resource.id,
    };
    if (resource.attributes && Object.keys(resource.attributes).length > 0) {
      body.attributes = resource.attributes;
    }
    if (
      resource.relationships &&
      Object.keys(resource.relationships).length > 0
    ) {
      body.relationships = this.extractRelationshipsIdentifiers(
        resource.relationships
      );
    }
    return this.http.patch<JsonApiOneResourceDocument<R>>(
      `${this.url}/${resource.type}/${resource.id}/transition/${transition}`,
      {
        data: body,
      }
    )
    .pipe(
      map((document) => {
        if (document && document.data) {
          resource.meta = document.data.meta;
          if (document.data.attributes) {
            resource.attributes = document.data.attributes;
          }
        }
        return new JsonApiOneResourceDocument(document);
      })
    );

  }

  delete(resource: JsonApiResource): Observable<void> {
    if (!resource.type) {
      throw new Error('You can not delete a resource without type');
    }
    if (!resource.id) {
      throw new Error('You can not delete a resource without id');
    }
    return this.http.delete<void>(
      `${this.url}/${resource.type}/${resource.id}`
    );
  }

  getRelationship(
    resource: JsonApiResource,
    name: string
  ): Observable<JsonApiOneIdentifierDocument> {
    if (!resource.type) {
      throw new Error('You can not get relationship a resource without type');
    }
    if (!resource.id) {
      throw new Error('You can not get relationship a resource without id');
    }
    return this.http
      .get<JsonApiDocumentInterface<JsonApiIdentifierInterface>>(
        `${this.url}/${resource.type}/${resource.id}/relationships/${name}`
      )
      .pipe(map((document) => new JsonApiOneIdentifierDocument(document)));
  }

  getRelationships(
    resource: JsonApiResource,
    name: string
  ): Observable<JsonApiManyIdentifiersDocument> {
    if (!resource.type) {
      throw new Error(
        'You can not get relationships of a resource without type'
      );
    }
    if (!resource.id) {
      throw new Error('You can not get relationships of a resource without id');
    }
    return this.http
      .get<JsonApiDocumentInterface<JsonApiIdentifierInterface[]>>(
        `${this.url}/${resource.type}/${resource.id}/relationships/${name}`
      )
      .pipe(map((document) => new JsonApiManyIdentifiersDocument(document)));
  }

  updateRelationship(
    resource: JsonApiResource,
    name: string,
    relationship: JsonApiResource
  ): Observable<void> {
    if (!resource.type) {
      throw new Error(
        'You can not update relationship of a resource without type'
      );
    }
    if (!resource.id) {
      throw new Error(
        'You can not update relationship of a resource without id'
      );
    }
    return this.http
      .patch<void>(
        `${this.url}/${resource.type}/${resource.id}/relationships/${name}`,
        {
          data: {
            type: relationship.type,
            id: relationship.id,
          },
        }
      )
      .pipe(
        tap(() => {
          const resourceObject = resource as Record<string, any>;
          resourceObject[name] = relationship;
        })
      );
  }

  updateRelationships(
    resource: JsonApiResource,
    name: string,
    relationships: JsonApiResource[]
  ): Observable<void> {
    if (!resource.type) {
      throw new Error(
        'You can not update relationships of a resource without type'
      );
    }
    if (!resource.id) {
      throw new Error(
        'You can not update relationships of a resource without id'
      );
    }

    const body = {
      data: relationships.map(({ type, id }) => ({ type, id })),
    };

    return this.http
      .patch<void>(
        `${this.url}/${resource.type}/${resource.id}/relationships/${name}`,
        body
      )
      .pipe(
        tap(() => {
          const resourceObject = resource as Record<string, any>;
          resourceObject[name] = relationships;
        })
      );
  }

  saveRelationships(
    resource: JsonApiResource,
    name: string,
    relationships: JsonApiResource[]
  ): Observable<void> {
    if (!resource.type) {
      throw new Error(
        'You can not save relationships of a resource without type'
      );
    }
    if (!resource.id) {
      throw new Error(
        'You can not save relationships of a resource without id'
      );
    }

    const body = {
      data: relationships.map(({ type, id }) => ({ type, id })),
    };

    return this.http
      .post<void>(
        `${this.url}/${resource.type}/${resource.id}/relationships/${name}`,
        body
      )
      .pipe(
        tap(() => {
          const resourceObject = resource as Record<string, any>;
          const oldRelationships = resourceObject[name] as JsonApiResource[];
          resourceObject[name] = oldRelationships
            .filter((relationship) =>
              oldRelationships.map((data) => data.id).includes(relationship.id)
            )
            .concat(relationships);
        })
      );
  }

  deleteRelationships(
    resource: JsonApiResource,
    name: string,
    relationships: JsonApiResource[]
  ): Observable<void> {
    if (!resource.type) {
      throw new Error(
        'You can not delete relationships of a resource without type'
      );
    }
    if (!resource.id) {
      throw new Error(
        'You can not delete relationships of a resource without id'
      );
    }
    return this.http
      .request<void>(
        'delete',
        `${this.url}/${resource.type}/${resource.id}/relationships/${name}`,
        {
          body: {
            data: relationships.map(({ type, id }) => ({ type, id })),
          },
        }
      )
      .pipe(
        tap(() => {
          const resourceObject = resource as Record<string, any>;
          if (resourceObject[name]) {
            const deletedRelationshipsIds = relationships.map(({ id }) => id);
            resourceObject[name] = (
              resourceObject[name] as JsonApiResource[]
            ).filter(
              (relationship) =>
                !deletedRelationshipsIds.includes(relationship.id)
            );
          }
        })
      );
  }

  private extractRelationshipsIdentifiers(
    relationships: JsonApiRelationships
  ): JsonApiRelationships {
    return Object.entries(relationships).reduce((acc, [name, { data }]) => {
      if (data instanceof Array) {
        return {
          ...acc,
          [name]: {
            data: data.map(({ type, id }) => ({ type, id })),
          },
        };
      } else {
        return {
          ...acc,
          [name]: {
            data: data ? { type: data.type, id: data.id } : null,
          },
        };
      }
    }, {});
  }

  private getResourceType(resourceType: ResourceType<any> | string): string {
    if (!resourceType) {
      throw new Error('Missing resource type');
    }
    if (typeof resourceType === 'string') {
      return resourceType;
    }
    if (!resourceType.prototype.type) {
      throw new Error(`Missing type for resource ${resourceType.name}`);
    }
    return resourceType.prototype.type;
  }

  getResources(
  ): Observable<{ data:any,jsonapi:any }> {
    return  this.http.get<{ data:any,jsonapi:any }>(`${this.url}/enums/list/resources`);
  }

  getResourcesEnumsAttributes(
    resource:string
  ): Observable<{ data: {attributes:string[]}, jsonapi:any }> {
    return  this.http.get<{ data:any,jsonapi:any }>(`${this.url}/enums/list/resources/${resource}`);
  }
}
